import { apiClient } from './client'
import type {
  PendingFeedbackListResponse,
  AdminFeedbackReviewRequest,
  FeedbackStats,
} from '@/types/api'

// Types
export interface PendingRequest {
  id: number
  user_email: string
  user_name: string
  requested_kbs: string[]
  reason: string
  requested_at: string
  status: string
}

export interface User {
  email: string
  name: string
  role: 'SuperUser' | 'Admin' | 'User'
  permissions: string[]
  is_active: boolean
  created_at: string | null
}

export interface UserStatistics {
  user_email: string
  user_name: string
  total_jobs: number
  completed_jobs: number
  failed_jobs: number
  articles_processed: number
  articles_pushed_to_servicenow: number
  job_success_rate: number
  push_success_rate: number
  last_activity: string | null
}

export interface KnowledgeBaseStatistics {
  knowledge_base: string
  total_jobs: number
  completed_jobs: number
  failed_jobs: number
  total_articles_processed: number
  articles_pushed_to_servicenow: number
  push_success_rate: number
  unique_users: number
  avg_processing_time_minutes: number
}

export interface AnalyticsSummary {
  total_users: number
  total_articles: number
  total_knowledge_bases: number
  evaluated_articles: number
  enhanced_articles: number
  evaluation_jobs: number
  enhancement_jobs: number
  completed_evaluation_jobs: number
  completed_enhancement_jobs: number
  enhancement_rate: number
}

export interface PromptSummary {
  id: string
  category: 'evaluation' | 'enhancement'
  subcategory: string
  name: string
  purpose: string
}

export interface PromptInput {
  name: string
  type: string
  description: string
}

export interface PromptVariant {
  article_type: string
  body: string
}

export interface PromptDetail extends PromptSummary {
  used_in: string
  inputs: PromptInput[]
  template: string
  variants: PromptVariant[]
  notes: string[]
  source_path: string
}

// Admin API service
export const adminApi = {
  // User Management
  async getPendingRequests(): Promise<PendingRequest[]> {
    const response = await apiClient.get('/v1/users/pending-requests')
    return response.data
  },

  async approveRequest(requestId: number, approvedKbs: string[]): Promise<void> {
    await apiClient.post(`/v1/users/requests/${requestId}/approve`, {
      approved_kbs: approvedKbs,
    })
  },

  async rejectRequest(requestId: number, reason?: string): Promise<void> {
    await apiClient.post(`/v1/users/requests/${requestId}/reject`, {
      reason: reason || 'Request rejected',
    })
  },

  async getAllUsers(): Promise<{ users: User[] }> {
    const response = await apiClient.get('/v1/users/all')
    return response.data
  },

  async updateUserRole(userEmail: string, newRole: string): Promise<void> {
    await apiClient.put('/v1/users/role', {
      user_email: userEmail,
      new_role: newRole,
    })
  },

  async updateUserPermissions(userEmail: string, knowledgeBases: string[]): Promise<void> {
    await apiClient.put('/v1/users/permissions', {
      user_email: userEmail,
      knowledge_bases: knowledgeBases,
    })
  },

  async deleteUser(userEmail: string): Promise<void> {
    await apiClient.delete('/v1/users/delete', {
      data: { user_email: userEmail },
    })
  },

  async getAvailableKnowledgeBases(): Promise<string[]> {
    const response = await apiClient.get('/v1/users/available-kbs')
    return response.data
  },

  // Analytics
  async getAnalyticsSummary(): Promise<AnalyticsSummary> {
    const response = await apiClient.get('/v1/analytics/summary')
    return response.data
  },

  async getUserStatistics(): Promise<{ users: UserStatistics[] }> {
    const response = await apiClient.get('/v1/analytics/users')
    return response.data
  },

  async getKnowledgeBaseStatistics(): Promise<{ knowledge_bases: KnowledgeBaseStatistics[] }> {
    const response = await apiClient.get('/v1/analytics/knowledge-bases')
    return response.data
  },

  async exportAnalytics(exportType: 'users' | 'knowledge_bases', format: 'csv' | 'excel'): Promise<Blob> {
    const response = await apiClient.get('/v1/analytics/export', {
      params: { export_type: exportType, format },
      responseType: 'blob',
    })
    return response.data
  },

  // ============================================================================
  // ISSUE FEEDBACK REVIEW
  // ============================================================================

  // Get pending feedback for review
  async getPendingFeedback(params?: {
    limit?: number
    offset?: number
  }): Promise<PendingFeedbackListResponse> {
    const response = await apiClient.get('/v1/admin/feedback/pending', { params })
    return response.data
  },

  // Get reviewed feedback (approved or rejected)
  async getReviewedFeedback(params: {
    status: 'approved' | 'rejected'
    limit?: number
    offset?: number
  }): Promise<PendingFeedbackListResponse> {
    const response = await apiClient.get('/v1/admin/feedback/reviewed', { params })
    return response.data
  },

  // Review feedback (approve/reject)
  async reviewFeedback(request: AdminFeedbackReviewRequest): Promise<{ status: string; decision: string }> {
    const response = await apiClient.post('/v1/admin/feedback/review', request)
    return response.data
  },

  // Get feedback statistics
  async getFeedbackStats(): Promise<FeedbackStats> {
    const response = await apiClient.get('/v1/admin/feedback/stats')
    return response.data
  },

  // ============================================================================
  // PROMPT CATALOG (read-only viewer)
  // ============================================================================

  async getPrompts(): Promise<{ prompts: PromptSummary[] }> {
    const response = await apiClient.get('/v1/admin/prompts')
    return response.data
  },

  async getPrompt(promptId: string): Promise<PromptDetail> {
    const response = await apiClient.get(`/v1/admin/prompts/${promptId}`)
    return response.data
  },
}

export default adminApi
